import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { Button } from 'primeng/button';
import { Avatar } from 'primeng/avatar';
import { Popover } from 'primeng/popover';
import { DatePicker } from 'primeng/datepicker';
import { Select } from 'primeng/select';
import { MenuItem } from 'primeng/api';
import { ComposeEmailOrientacoesComponent } from '@/features/solicitacoes/orientacoes-tecnicas/dashboard-orientacoes-tecnicas/components/dialogs/compose-email-orientacoes/compose-email-orientacoes';
import { InserirTarefaOrientacoesComponent } from '@/features/solicitacoes/orientacoes-tecnicas/dashboard-orientacoes-tecnicas/components/dialogs/inserir-tarefa-orientacoes/inserir-tarefa-orientacoes.component';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { EInconPos } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { Card } from 'primeng/card';
import { BotaoAcoes } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { LoggerService } from '@/core/services/logger.service';

interface Contact {
    id: number;
    name: string;
    image: string;
    status: 'online' | 'busy' | 'offline';
    statusLabel?: string;
    lastMessage: string;
    date: Date;
    unread?: number;
}

interface Message {
    text: string;
    owner: 'me' | 'other';
    createdAt: string;
}

/** Tela (mock) de detalhe/chat de uma solicitação de Orientação Técnica: contatos, mensagens e ações. */
@Component({
    selector: 'app-dashboard-orientacoes-tecnicas',
    standalone: true,
    imports: [CommonModule, FormsModule, InputText, Button, Avatar, Popover, DatePicker, Select, SelectEstado, Card, BotaoAcoes, ComposeEmailOrientacoesComponent, InserirTarefaOrientacoesComponent],
    templateUrl: './orientacoes-tecnicas-detalhe.html',
    styleUrls: ['./orientacoes-tecnicas-detalhe.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrientacoesTecnicasDetalhe implements OnInit {
    private readonly logger = inject(LoggerService);

    currentUser = {
        name: 'CFM',
        image: 'images/logos/logo.png'
    };

    /** Contato/solicitação atualmente selecionado (setado por seleção e pelas ações do menu). */
    readonly activeContact = signal<Contact>({
        id: 6,
        name: 'CRM SP',
        image: 'images/estados/SP.png',
        status: 'online',
        statusLabel: 'Novo',
        lastMessage: 'Sed do eiusmod tempor...',
        date: new Date(2025, 2, 19)
    });

    contacts: Contact[] = [
        this.activeContact(),
        { id: 5, name: 'CRM SC', image: 'images/estados/SC.png', status: 'busy', statusLabel: 'Atribuído', lastMessage: 'Consequat mauris nunc...', date: new Date(2025, 2, 19) },
        { id: 4, name: 'CRM SE', image: 'images/estados/SE.png', status: 'offline', statusLabel: 'Pendente', lastMessage: 'Adipiscing tristique risu...', date: new Date(2025, 2, 19) },
        { id: 3, name: 'CRM RJ', image: 'images/estados/RJ.png', status: 'online', statusLabel: 'Solucionado', lastMessage: 'Quis nostrud exercitatio...', date: new Date(2025, 2, 19) },
        { id: 2, name: 'CRM MG', image: 'images/estados/MG.png', status: 'busy', statusLabel: 'Fechado', lastMessage: 'Excepteur sint occaeca...', date: new Date(2025, 2, 19) },
        { id: 1, name: 'CRM SP', image: 'images/estados/SP.png', status: 'busy', statusLabel: 'Solucionado', lastMessage: 'Resolvido', date: new Date(2025, 2, 19) }
    ];

    readonly filteredContacts = signal<Contact[]>([]);
    filterDate: Date | null = null;
    filterStatus: string | null = null;
    filterMessage: string = '';
    // UF selecionada (seletor padrão do sistema). BR significa "todas" por padrão
    filterUf: string = 'BR';

    statusOptions = [
        { label: 'Novo', value: 'Novo' },
        { label: 'Atribuído', value: 'Atribuído' },
        { label: 'Pendente', value: 'Pendente' },
        { label: 'Solucionado', value: 'Solucionado' },
        { label: 'Fechado', value: 'Fechado' }
    ];

    readonly messages = signal<Message[]>([]);

    messagesMap: { [key: number]: Message[] } = {
        6: [
            { text: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit', owner: 'other', createdAt: ' 19 de Março de 2025 ás 17:25' },
            { text: 'Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua', owner: 'other', createdAt: '19 de Março de 2025 17:26' },
            { text: 'Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua', owner: 'me', createdAt: ' 19 de Março de 2025 17:26' }
        ],
        5: [
            { text: 'Olá, como está o processo?', owner: 'other', createdAt: '09:00' },
            { text: 'Estamos analisando.', owner: 'me', createdAt: '09:05' }
        ],
        4: [{ text: 'Preciso de ajuda com o documento X', owner: 'other', createdAt: '14:20' }],
        3: [
            { text: 'Bom dia', owner: 'other', createdAt: '08:00' },
            { text: 'Bom dia, em que posso ajudar?', owner: 'me', createdAt: '08:10' }
        ],
        2: [{ text: 'O dia hoje está bacana', owner: 'other', createdAt: '14:20' }],
        1: [{ text: 'Resolvido, Parabéns', owner: 'other', createdAt: '14:20' }]
    };

    newMessage: string = '';

    // Controle do diálogo de composição
    readonly showCompose = signal(false);
    readonly composeRemetente = signal<{ nome: string; avatar: string }>({ nome: '', avatar: '' });
    readonly selectedMessageTitle = signal('');

    protected readonly EInconPos = EInconPos;

    // Controle do diálogo de inserir tarefa
    readonly showInserirTarefa = signal(false);

    actionItems: MenuItem[] = [
        {
            label: 'Inserir mensagem',
            icon: 'pi pi-file-edit',
            styleClass: 'bg-blue-500',
            command: () => this.visualizarMensagem({ text: 'Resposta a solicitação ID 2788', owner: 'me', createdAt: 'string' })
        },
        {
            label: 'Atribuir Tarefa',
            icon: 'pi pi-user-plus',
            styleClass: 'bg-purple-500',
            command: () => this.showInserirTarefa.set(true)
        },
        {
            label: 'Encaminhar',
            icon: 'pi pi-send',
            styleClass: 'bg-blue-500',
            command: () => this.encaminhar()
        },
        {
            label: 'Solucionar',
            icon: 'pi pi-check-circle',
            styleClass: 'bg-green-500',
            command: () => this.marcarSolucionado()
        },
        {
            label: 'Encerrar',
            icon: 'pi pi-times-circle',
            styleClass: 'bg-red-500',
            command: () => this.encerrar()
        },
        {
            label: 'Outras informações',
            icon: 'pi pi-info-circle',
            styleClass: 'bg-gray-500',
            command: () => this.visualizarOutrasInformacoes()
        }
    ];

    ngOnInit(): void {
        this.filteredContacts.set([...this.contacts]);
        this.selectContact(this.activeContact());
    }

    applyFilters() {
        this.filteredContacts.set(
            this.contacts.filter((contact) => {
                let matchDate = true;
                if (this.filterDate) {
                    const contactDate = contact.date;
                    matchDate = contactDate.getDate() === this.filterDate.getDate() && contactDate.getMonth() === this.filterDate.getMonth() && contactDate.getFullYear() === this.filterDate.getFullYear();
                }

                let matchStatus = true;
                if (this.filterStatus) {
                    matchStatus = contact.statusLabel === this.filterStatus;
                }

                let matchMessage = true;
                if (this.filterMessage) {
                    matchMessage = contact.lastMessage.toLowerCase().includes(this.filterMessage.toLowerCase());
                }

                let matchUf = true;
                if (this.filterUf && this.filterUf !== 'BR') {
                    const ufContato = this.getContactUf(contact);
                    matchUf = ufContato === this.filterUf;
                }

                return matchDate && matchStatus && matchMessage && matchUf;
            })
        );
    }

    clearFilters() {
        this.filterDate = null;
        this.filterStatus = null;
        this.filterMessage = '';
        this.filterUf = 'BR';
        this.filteredContacts.set([...this.contacts]);
    }

    selectContact(contact: Contact) {
        this.activeContact.set(contact);
        this.messages.set(this.messagesMap[contact.id] || []);
    }

    sendMessage() {
        if (this.newMessage.trim()) {
            const nova: Message = {
                text: this.newMessage,
                owner: 'me',
                createdAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            };
            this.messages.update((atuais) => [...atuais, nova]);
            this.newMessage = '';
        }
    }

    visualizarMensagem(msg: Message) {
        // Define remetente conforme a origem da mensagem
        const contato = this.activeContact();
        if (msg.owner === 'other') {
            this.composeRemetente.set({ nome: contato.name, avatar: contato.image });
        } else {
            this.composeRemetente.set({ nome: this.currentUser.name, avatar: this.currentUser.image });
        }
        this.selectedMessageTitle.set(msg.text);
        this.showCompose.set(true);
    }

    responderMensagem(msg: Message) {
        this.visualizarMensagem(msg);
    }

    // Handlers de ações (stubs mínimos)
    encaminhar() {
        this.logger.info('Ação: Encaminhar', this.activeContact());
        this.activeContact.update((contato) => ({ ...contato, statusLabel: 'Atribuido' }));
    }

    encerrar() {
        this.logger.info('Ação: Encerrar', this.activeContact());
        this.activeContact.update((contato) => ({ ...contato, statusLabel: 'Fechado' }));
    }

    marcarSolucionado() {
        this.logger.info('Ação: Solucionado', this.activeContact());
        this.activeContact.update((contato) => ({ ...contato, statusLabel: 'Solucionado' }));
    }

    visualizarOutrasInformacoes() {
        this.logger.info('Ação: Visualizar outras informações', this.activeContact());
    }

    getBadgeClass(status: string | undefined): string {
        if (!status) return '';
        const map: { [key: string]: string } = {
            Novo: 'novo',
            Atribuído: 'atribuido',
            Pendente: 'pendente',
            Solucionado: 'solucionado',
            Fechado: 'fechado'
        };
        return map[status] || '';
    }

    private getContactUf(contact: Contact): string {
        // Tenta extrair a UF pelo nome (ex.: "CRM SP")
        const parts = contact.name.trim().split(/\s+/);
        const last = parts[parts.length - 1]?.toUpperCase();
        if (last && /^[A-Z]{2}$/.test(last)) {
            return last;
        }
        // Fallback: tenta pelo caminho da imagem (ex.: images/estados/SP.png)
        const match = contact.image.match(/\/([A-Z]{2})\.png$/i) || contact.image.match(/([A-Z]{2})\.png$/i);
        if (match && match[1]) {
            return match[1].toUpperCase();
        }
        return 'BR';
    }

    onFileChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length) {
            this.logger.info('Arquivo alterado', input.files[0].name);
        }
    }

    onImageChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length) {
            this.logger.info('Imagem alterada', input.files[0].name);
        }
    }
}
